/**
 * Interface representing parsed vartype information
 */
export type VarTypeInfo = {
    id: string;
    name: string;
    description: string;
};

/**
 * Interface representing a metadata field in a variable sub-package
 */
export type Metadata = {
    type: number;
    value: string;
};

/**
 * Interface representing a parsed variable sub-package
 */
export type VariableSubPackage = {
    varType: string;
    varTypeInfo: VarTypeInfo | undefined;
    value: number;
    prefix: string;
    metadata: Array<Metadata>;
};

/**
 * Interface representing a parsed data package
 */
export type PackageParseResult = {
    variables: Array<VariableSubPackage>;
    rawPackage: string;
};

/**
 * SI prefix multipliers
 */
const SI_PREFIX_MULTIPLIERS: Record<string, number> = {
    'i': 1, // integer (no prefix)
    'a': 1e-18, // atto
    'f': 1e-15, // femto
    'p': 1e-12, // pico
    'n': 1e-9, // nano
    'u': 1e-6, // micro
    'm': 1e-3, // milli
    ' ': 1, // no prefix (space) = 10^0
    'k': 1e3, // kilo
    'M': 1e6, // mega
    'G': 1e9, // giga
    'T': 1e12, // tera
    'P': 1e15, // peta
    'E': 1e18, // exa
};

/**
 * Converts a raw value with SI prefix to actual numeric value
 * @param value - The raw integer value
 * @param prefix - The SI prefix character
 * @returns The actual numeric value with SI prefix applied
 */
export function applyPrefix(value: number, prefix: string): number {
    const multiplier = SI_PREFIX_MULTIPLIERS[prefix];
    if (multiplier === undefined) {
        throw new Error(`Unknown SI prefix: ${prefix}`);
    }
    return value * multiplier;
}

/**
 * Formats a value with SI prefix for display
 * @param value - The raw integer value
 * @param prefix - The SI prefix character
 * @param unit - The unit symbol (e.g., 'V', 'A', 's')
 * @returns Formatted string with value and unit
 */
export function formatValue(value: number, prefix: string, unit?: string): string {
    const actualValue = applyPrefix(value, prefix);
    const unitStr = unit ?? '';

    if (prefix === 'i') {
        return `${value}${unitStr}`;
    }

    return `${actualValue.toExponential(3)} ${unitStr}`;
}

/**
 * Interprets metadata type and value into human-readable string
 * @param metadata - The metadata object
 * @returns Human-readable description of the metadata
 */
export function interpretMetadata(metadata: Metadata): string {
    const hexValue = metadata.value;
    const decValue = parseInt(hexValue, 16);

    switch (metadata.type) {
        case METADATA_TYPE_STATUS: {
            // Status metadata
            const statuses: Array<string> = [];
            if (decValue === 0) {
                statuses.push('OK');
            } else {
                // eslint-disable-next-line no-bitwise -- Bitwise operations required for flag parsing
                if (decValue & STATUS_FLAG_TIMING_NOT_MET) statuses.push('Timing not met');
                // eslint-disable-next-line no-bitwise -- Bitwise operations required for flag parsing
                if (decValue & STATUS_FLAG_OVERLOAD) statuses.push('Overload');
                // eslint-disable-next-line no-bitwise -- Bitwise operations required for flag parsing
                if (decValue & STATUS_FLAG_UNDERLOAD) statuses.push('Underload');
                // eslint-disable-next-line no-bitwise -- Bitwise operations required for flag parsing
                if (decValue & STATUS_FLAG_OVERLOAD_WARNING) statuses.push('Overload warning');
            }
            return `Status: ${statuses.join(', ')}`;
        }
        case METADATA_TYPE_RANGE:
            // Range metadata
            return `Range: 0x${hexValue} (${decValue})`;
        case METADATA_TYPE_NOISE:
            // Noise metadata
            return `Noise level: ${decValue}`;
        default:
            return `Unknown metadata type ${metadata.type}: 0x${hexValue}`;
    }
}

/**
 * Parser for MethodScript data packages
 */
/**
 * Sub-package field offsets and sizes
 */
/** Start index of the vartype field (2 hex characters) */
const VARTYPE_START = 0;
/** End index of the vartype field */
const VARTYPE_END = 2;
/** End index of the hex value field (7 hex characters after vartype) */
const HEX_VALUE_END = 9;
/** Minimum length of a valid variable sub-package string */
const MIN_SUBPACKAGE_LENGTH = 10;
/**
 * Offset applied to raw hex values.
 * MethodScript uses a signed representation where 0x8000000 represents zero.
 */
const HEX_VALUE_ZERO_OFFSET = 0x8000000;

/**
 * Status flag bit masks for metadata interpretation
 */
/** Bit 0: Timing requirement was not met */
const STATUS_FLAG_TIMING_NOT_MET = 1;
/** Bit 1: Measured value exceeds the supported range */
const STATUS_FLAG_OVERLOAD = 2;
/** Bit 2: Measured value is below the supported range */
const STATUS_FLAG_UNDERLOAD = 4;
/** Bit 3: Measured value is approaching the range limit */
const STATUS_FLAG_OVERLOAD_WARNING = 8;

/**
 * Metadata type identifiers
 */
/** Status metadata: contains status flags */
const METADATA_TYPE_STATUS = 1;
/** Range metadata: contains current range info */
const METADATA_TYPE_RANGE = 2;
/** Noise metadata: contains noise level info */
const METADATA_TYPE_NOISE = 4;

export class MethodScriptPackageParser {
    private varTypeMap: Map<string, VarTypeInfo>;

    /**
     * Creates a new MethodScriptPackageParser
     * @param varTypes - Array of vartype information
     */
    constructor(varTypes: Array<VarTypeInfo>) {
        this.varTypeMap = new Map();
        for (const varType of varTypes) {
            this.varTypeMap.set(varType.id, varType);
        }
    }

    /**
     * Gets vartype information by ID
     * @param id - The vartype ID (e.g., 'ab', 'ba')
     * @returns The vartype information or undefined if not found
     */
    public getVarTypeInfo(id: string): VarTypeInfo | undefined {
        return this.varTypeMap.get(id);
    }

    /**
     * Checks if a string is a complete data package (starts with P and ends with \n)
     * @param data - The data string to check
     * @returns true if the data is a complete package
     */
    public isCompletePackage(data: string): boolean {
        return data.startsWith('P') && data.endsWith('\n');
    }

    /**
     * Parses a data package string
     * @param packageStr - The package string (e.g., "Pda8000800u;ba8000800u,10,20B\n")
     * @returns The parsed data package
     * @throws {Error} If the package format is invalid
     */
    public parsePackage(packageStr: string): PackageParseResult {
        if (!this.isCompletePackage(packageStr)) {
            throw new Error('Invalid package format: must start with P and end with \\n');
        }

        // Remove 'P' prefix and '\n' suffix
        const content = packageStr.slice(1, -1);

        // Split by semicolon to get variable sub-packages
        const varStrings = content.split(';');

        const variables: Array<VariableSubPackage> = [];

        for (const varStr of varStrings) {
            if (varStr.trim() === '') {
                continue;
            }
            variables.push(this.parseVariableSubPackage(varStr));
        }

        return {
            variables,
            rawPackage: packageStr,
        };
    }

    /**
     * Parses a variable sub-package string
     * @param varStr - The variable sub-package string (e.g., "ba8000800u,10,20B")
     * @returns The parsed variable sub-package
     * @throws Error if the variable sub-package format is invalid
     */
    private parseVariableSubPackage(varStr: string): VariableSubPackage {
        // Split by comma to separate variable value from metadata
        const parts = varStr.split(',');
        const varPart = parts[0];

        if (varPart.length < MIN_SUBPACKAGE_LENGTH) {
            throw new Error(`Invalid variable sub-package format: ${varStr}`);
        }

        // Extract vartype (first 2 characters)
        const varType = varPart.slice(VARTYPE_START, VARTYPE_END);

        // Extract hex value (next 7 characters)
        const hexValue = varPart.slice(VARTYPE_END, HEX_VALUE_END);

        // Extract prefix (must be exactly one character)
        const prefix = varPart.slice(HEX_VALUE_END);
        if (prefix.length !== 1 || !(prefix in SI_PREFIX_MULTIPLIERS)) {
            throw new Error(`Invalid SI prefix in package data: '${prefix}'`);
        }

        // Validate hex string contains only valid hex characters
        if (!/^[0-9a-fA-F]+$/.test(hexValue)) {
            throw new Error(`Invalid hex value in package data: '${hexValue}'`);
        }

        // Parse hex value
        const rawValue = parseInt(hexValue, 16);
        const adjustedValue = rawValue - HEX_VALUE_ZERO_OFFSET;

        // Parse metadata
        const metadata: Array<Metadata> = [];
        for (let i = 1; i < parts.length; i++) {
            const metaPart = parts[i];
            if (metaPart.length < 2) {
                throw new Error(`Invalid metadata format: ${metaPart}`);
            }

            const type = parseInt(metaPart[0], 10);
            if (Number.isNaN(type)) {
                throw new Error(`Invalid metadata type in package data: '${metaPart[0]}'`);
            }
            const value = metaPart.slice(1);

            metadata.push({ type, value });
        }

        // Look up vartype info
        const varTypeInfo = this.getVarTypeInfo(varType);

        return {
            varType,
            varTypeInfo,
            value: adjustedValue,
            prefix,
            metadata,
        };
    }

    /**
     * Extracts complete packages from a data stream
     * @param data - The data stream containing one or more packages
     * @returns Array of complete package strings
     */
    public extractPackages(data: string): Array<string> {
        const packages: Array<string> = [];
        const currentChars: Array<string> = [];
        let isInPackage = false;

        for (const char of data) {
            if (char === 'P' && !isInPackage) {
                isInPackage = true;
                currentChars.length = 0;
                currentChars.push('P');
            } else if (isInPackage) {
                currentChars.push(char);
                if (char === '\n') {
                    packages.push(currentChars.join(''));
                    currentChars.length = 0;
                    isInPackage = false;
                }
            }
        }

        return packages;
    }
}
